<template>
  <div class="steps-indicator p-4 d-flex flex-column align-items-center align-items-sm-start">
    <h6 class="mb-4 text-muted">
      {{ isAlreadyApproved ? 'Please complete the following to start participating again' : 'Few pending steps before you can start participating' }}
    </h6>

    <!-- Steps Indicators -->
    <div class="steps-indicator__steps">
      <div 
        v-for="(step, index) in steps" 
        :key="index" 
        class="steps-indicator__step"
        :class="{
          'steps-indicator__step--completed': index < currentStepIndex,
          'steps-indicator__step--current': index === currentStepIndex,
          'steps-indicator__step--pending': index > currentStepIndex,
        }"
      >
        <!-- Circle with number -->
        <div 
          class="steps-indicator__circle"
          :class="{
            'steps-indicator__circle--completed': index < currentStepIndex,
            'steps-indicator__circle--current': index === currentStepIndex,
            'steps-indicator__circle--pending': index > currentStepIndex
          }"
        >
          <span class="steps-indicator__circle-number">{{ index + 1 }}</span>
        </div>

        <!-- Step Label -->
        <div class="steps-indicator__label ml-sm-2">
          <div 
            class="steps-indicator__title"
            :class="{
              'steps-indicator__title--completed': index < currentStepIndex,
              'steps-indicator__title--current': index === currentStepIndex,
              'steps-indicator__title--pending': index > currentStepIndex
            }"
          >
            {{ step.label }}
          </div>
        </div>

        <!-- Connector line (not after last step) -->
        <div 
          v-if="index < steps.length - 1" 
          class="steps-indicator__connector"
          :class="{
            'steps-indicator__connector--completed': index < currentStepIndex,
            'steps-indicator__connector--pending': index >= currentStepIndex
          }"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StepsIndicator',

  props: {
    steps: {
      type: Array,
      required: true,
      // Example: [{ label: 'Contract', id: 'contract' }]
    },
    currentStep: {
      type: String,
      required: true
    },
    showStatus: {
      type: Boolean,
      default: true
    },
    isAlreadyApproved: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isMobile: false
    }
  },
  computed: {
    currentStepIndex() {
      return this.steps.findIndex(step => step.id === this.currentStep)
    },
  },
  mounted() {
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth < 768
    }
  }
}
</script>

<style lang="scss" scoped>
.steps-indicator {
  width: 100%;
  max-width: 300px; // Narrower for vertical layout
  background: var(--component-steps-indicator-bg);
  
  @media (max-width: 767px) {
    max-width: 100%; // Full width on mobile
    overflow-x: auto;
  }

  &__steps {
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-y: hidden;
    
    @media (max-width: 767px) {
      flex-direction: row;
      overflow-x: auto;
      overflow-y: visible;
      padding-bottom: 10px; // Space for scrollbar
    }
  }

  &__step {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    position: relative;
    margin-bottom: 30px;
    transition: opacity 0.3s ease;

    &:last-child {
      margin-bottom: 0;
    }
    
    @media (max-width: 767px) {
      flex-direction: column;
      align-items: center;
      margin-bottom: 0;
      margin-right: 20px;
      flex: 0 0 auto;
      width: 80px; // Fixed width for mobile steps
      
      &:last-child {
        margin-right: 0;
      }
    }
  }

  &__circle {
    width: 40px;
    height: 40px;
    min-width: 40px; // Ensure circle doesn't shrink
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2;
    transition: all 0.3s ease;

    &--completed {
      background-color: #034bae;
    }

    &--current {
      background-color: #0069ff;
      font-weight: bold;
    }

    &--pending {
      background-color: #ffffff;
      border: 2px solid #e6e9ed;
    }

    &-number {
      font-size: 16px;
    }
  }

  &__circle--completed &__circle-number,
  &__circle--current &__circle-number {
    color: #ffffff;
  }

  &__circle--pending &__circle-number {
    color: #6b7280;
  }

  &__connector {
    position: absolute;
    left: 20px; // Center of the circle
    top: 40px; // Bottom of the circle
    width: 2px;
    height: 30px; // Connects to the next circle
    z-index: 1;

    &--completed {
      background-color: #0069ff;
    }

    &--pending {
      background-color: #e6e9ed;
    }
    
    @media (max-width: 767px) {
      left: 54px;
      top: 20px;
      width: 70px; // Width to reach the next circle
      height: 2px; // Thin horizontal line
    }
  }

  &__label {
    text-align: left;
    padding-top: 10px;
    flex: 1;
    
    @media (max-width: 767px) {
      text-align: center;
      padding-top: 8px;
    }
  }

  &__title {
    font-size: 14px;

    &--current {
      font-weight: bold;
      color: #ffffff;
    }

    &--completed {
      color: #ffffff;
    }

    &--pending {
      color: #6b7280;
    }
    
    @media (max-width: 767px) {
      font-size: 12px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 70px;

      &--current {
        max-width: 90px;
      }
    }
  }

  &__subtitle {
    font-size: 14px;

    &--current {
      font-weight: bold;
      color: #333333;
    }

    &--completed {
      color: #333333;
    }

    &--pending {
      color: #6b7280;
    }
  }

  &__status {
    display: flex;
    align-items: center;
    background-color: #e9f5ff;
    border-radius: 6px;
    padding: 12px 15px;
    width: fit-content;
    margin-top: 20px;
  }

  &__status-icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: #0069ff;
    color: #ffffff;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 10px;
    font-weight: bold;
  }

  &__status-text {
    color: #0069ff;
    font-size: 14px;
  }

  // Mobile styles
  @media (max-width: 767px) {
    &__step {
      &--current {
        opacity: 1;
      }
    }

    &__status {
      width: 100%;
    }
  }

  // Extra small screens
  @media (max-width: 480px) {
    &__circle {
      width: 36px;
      height: 36px;
      min-width: 36px;
    }
  }
}
</style>